<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});
</script>

<template>
  <button
    class="emt-trilho"
    :class="{ 'emt-trilho--privada': isPrivate }"
    :disabled="disabled || isReplyRestricted"
    @click="$emit('toggleMode')"
  >
    <div
      ref="wootEditorReplyMode"
      class="emt-lado"
      :class="{ 'emt-lado--on': !isPrivate }"
    >
      <i class="i-lucide-send emt-lado__ico" aria-hidden="true" />
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div
      ref="wootEditorPrivateMode"
      class="emt-lado"
      :class="{ 'emt-lado--on': isPrivate }"
    >
      <i class="i-lucide-lock emt-lado__ico" aria-hidden="true" />
      <!-- No celular, "Privada": com o rótulo inteiro os atalhos do lado
           desciam para uma linha só deles (07/10/2026). A pílula mede o
           tamanho real, então acompanha a troca. -->
      <span class="emt-longo">{{
        $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE')
      }}</span>
      <span class="emt-curto">
        {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_CURTO') }}
      </span>
    </div>
    <div
      class="emt-pilula"
      :class="{ 'emt-pilula--parada': disabled || isReplyRestricted }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>

<style scoped>
.emt-curto {
  display: none;
}

@media (max-width: 639px) {
  .emt-longo {
    display: none;
  }
  .emt-curto {
    display: inline;
  }
}

/* Responder x Mensagem Privada.
 *
 * A pilula que desliza ja existia, mas era BRANCA nos dois lados: o controle
 * dizia "voce esta aqui" e nao dizia "o que voce esta prestes a fazer".
 *
 * E a diferenca entre os dois lados e a maior desta tela — um manda para o
 * CLIENTE, o outro fica dentro do escritorio. Escrever no lugar errado e o
 * pior erro possivel aqui. Entao a pilula assume o TOM do modo: azul quando
 * a mensagem vai sair, ambar quando fica interna. Ambar ja e a cor de nota
 * privada no produto inteiro — a bolha, a mensagem na linha do tempo.
 *
 * O trilho inteiro tambem esquenta em ambar no modo privado: a pessoa esta
 * olhando o campo de texto, nao o botao, e o entorno inteiro precisa avisar. */
.emt-trilho {
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  width: auto;
  height: 32px;
  padding: 4px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 999px;
  background: rgb(var(--slate-2));
  cursor: pointer;
  transition:
    background 0.24s ease,
    border-color 0.24s ease;
}

.emt-trilho:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.emt-trilho:active {
  transform: scale(0.995);
}

.emt-trilho:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.emt-trilho--privada {
  border-color: rgb(var(--amber-6));
  background: rgb(var(--amber-2));
}

.emt-lado {
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 10px;
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
  color: rgb(var(--slate-11));
  transition: color 0.24s ease;
}

.emt-lado__ico {
  display: block;
  width: 13px;
  height: 13px;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}

/* O texto do lado escolhido so vira branco DEPOIS que a pilula chega — igual
   ao que a barra de abas faz. Sem o atraso, branco sobre fundo claro e texto
   invisivel durante o percurso. */
.emt-lado--on {
  animation: emtTextoChega 0.3s ease forwards;
}

@keyframes emtTextoChega {
  0%,
  58% {
    color: rgb(var(--slate-12));
  }

  100% {
    color: rgb(255 255 255);
  }
}

/* Branco sobre o azul-9 tem contraste de sobra. Sobre o AMBAR-9, nao: medido
   em 255 197 61, branco da ~1.9:1 — ilegivel. Amarelo pede letra escura, e
   este e o rotulo que avisa que a mensagem NAO vai para o cliente; ele
   precisa ser lido de relance.
   
   Duas animacoes separadas, e nao uma so com `var()` no fim: variavel dentro
   de `@keyframes` NAO e reavaliada quando muda de valor — a animacao ja tinha
   sido computada com o branco e continuava branca mesmo com a variavel
   trocada. Medido: `--emt-tinta` valia `rgb(79 52 34)` no elemento e a cor
   final continuava `rgb(255,255,255)`. */
@keyframes emtTextoChegaEscuro {
  0%,
  58% {
    color: rgb(var(--slate-12));
  }

  100% {
    color: rgb(var(--amber-12));
  }
}

.emt-trilho--privada .emt-lado--on {
  animation-name: emtTextoChegaEscuro;
}

.emt-pilula {
  position: absolute;
  height: 24px;
  width: var(--chip-width);
  border-radius: 999px;
  background: rgb(var(--blue-9));
  box-shadow: 0 1px 2px rgb(var(--blue-9) / 0.35);
  transform: translateX(var(--translate-x));
  transition:
    transform 0.3s cubic-bezier(0.34, 1.12, 0.44, 1),
    width 0.3s cubic-bezier(0.34, 1.12, 0.44, 1),
    background 0.24s ease;
}

.emt-trilho--privada .emt-pilula {
  background: rgb(var(--amber-9));
  box-shadow: 0 1px 2px rgb(var(--amber-9) / 0.35);
}

/* Enquanto o controle esta travado, a pilula nao desliza: mover sozinha uma
   marca que a pessoa nao pode mudar promete uma interacao que nao existe. */
.emt-pilula--parada {
  transition: none;
}

[dir='rtl'] .emt-pilula {
  transform: translateX(var(--rtl-translate-x));
}

@media (prefers-reduced-motion: reduce) {
  .emt-trilho,
  .emt-lado,
  .emt-pilula {
    transition: none;
  }

  .emt-lado--on {
    animation: none;
    color: rgb(255 255 255);
  }

  .emt-trilho--privada .emt-lado--on {
    animation: none;
    color: rgb(var(--amber-12));
  }
}
</style>
